import { useTranslation } from 'react-i18next';
import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';
import { cn } from '@/core/lib/utils';
import { TEMPLATE_SECTOR } from '../types';
import type { TemplateSector } from '../types';

interface TemplateSectorFilterProps {
  value: TemplateSector | 'all';
  onChange: (value: TemplateSector | 'all') => void;
}

/**
 * Unboxed pill filter — deliberately not `PillRadioGroup` (that component's shared
 * gray container box is right for its existing form-field usages in `time-off`,
 * but doesn't match this gallery filter's design: individual chips with no shared
 * background, only the selected one filled). Uses the same Radix `RadioGroup`
 * primitive directly so `data-state` semantics stay identical.
 */
export function TemplateSectorFilter({ value, onChange }: TemplateSectorFilterProps) {
  const { t } = useTranslation('projectTemplates');

  const options: Array<{ value: TemplateSector | 'all'; label: string }> = [
    { value: 'all', label: t('gallery.sectorFilter.all') },
    ...Object.values(TEMPLATE_SECTOR).map((sector) => ({
      value: sector,
      label: t(`gallery.sectorFilter.${sector}`),
    })),
  ];

  return (
    <RadioGroupPrimitive.Root
      value={value}
      onValueChange={(next) => onChange(next as TemplateSector | 'all')}
      className="flex flex-wrap items-center gap-1"
      data-testid="project-templates-sector-filter"
    >
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          className={cn(
            'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
            'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'data-[state=unchecked]:text-muted-foreground data-[state=unchecked]:hover:bg-accent data-[state=unchecked]:hover:text-foreground',
            'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
          )}
          data-testid={`project-templates-sector-filter-option-${option.value}`}
        >
          {option.label}
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
